import { useEffect, useState, useRef } from 'react';

import {
  Field,
  FieldRenderProps,
  FormRenderProps,
  FormMeta,
  ValidateTrigger,
} from '@flowgram.ai/free-layout-editor';
import { JsonSchemaEditor } from '@flowgram.ai/form-materials';
import { TextArea, Button, Spin, Tag, Toast } from '@douyinfe/semi-ui';
import { IconHelpCircle, IconCopy } from '@douyinfe/semi-icons';

import { getFieldValue, setFieldValue } from '../../utils/common';
import { FlowNodeJSON, JsonSchema } from '../../typings';
import { getFeishuTableMetaData } from '../../https/feishu-api';
import { useIsSidebar } from '../../hooks';
import { CodeEditor } from '../../form-components/form-inputs';
import { FormHeader, FormContent, FormOutputs, FormItem } from '../../form-components';

export const renderForm = ({ form }: FormRenderProps<FlowNodeJSON>) => {
  const isSidebar = useIsSidebar();

  const [link, setLink] = useState(getFieldValue('link', form));
  const [jsCode, setJsCode] = useState(getFieldValue('jsCode', form));
  const [pushResult, setPushResult] = useState('');

  // 飞书表格元数据相关状态
  const [isLoadingMetaData, setIsLoadingMetaData] = useState(false);
  const [metaDataError, setMetaDataError] = useState<string | null>(null);
  const [tableMetaData, setTableMetaData] = useState<any>(null);
  const prevLinkRef = useRef<string>('');

  // 复制单个字段名
  const copyFieldName = async (fieldName: string) => {
    try {
      await navigator.clipboard.writeText(fieldName);
      Toast.success(`已复制字段名: ${fieldName}`);
    } catch (error) {
      Toast.error('复制失败');
    }
  };

  // 复制所有字段名
  const copyAllFieldNames = async () => {
    if (!tableMetaData?.columnNames || tableMetaData.columnNames.length === 0) {
      Toast.warning('没有可复制的字段名');
      return;
    }
    try {
      const allFields = tableMetaData.columnNames.join(', ');
      await navigator.clipboard.writeText(allFields);
      Toast.success(`已复制所有字段名: ${allFields}`);
    } catch (error) {
      Toast.error('复制失败');
    }
  };

  // 处理飞书表格链接失去焦点事件
  const handleLinkBlur = async () => {
    const currentUrl = link?.trim();
    setMetaDataError(null);

    // 如果URL为空或与上次相同，则不处理
    if (!currentUrl) {
      return;
    }

    // 简单验证URL格式
    if (!currentUrl.startsWith('http')) {
      setMetaDataError('请输入有效的飞书通知链接，应以http或https开头');
      return;
    }

    // 如果URL与上次相同，则不重复请求
    if (currentUrl === prevLinkRef.current) {
      return;
    }

    // 更新上次的URL值
    prevLinkRef.current = currentUrl;

    // 开始加载
    setIsLoadingMetaData(true);
    setTableMetaData(null);

    try {
      // 调用接口获取表格元数据
      const response = await getFeishuTableMetaData({ url: currentUrl });

      // 确保正确获取 data 字段
      if (response && response.data) {
        // 验证返回的数据格式
        if (!response.data.tableName || !Array.isArray(response.data.columnNames)) {
          throw new Error('表格元数据格式不完整');
        }
        setTableMetaData(response.data);
        // 保存元数据到表单
        setFieldValue('objToken', response.data.objToken, form);
        setFieldValue('tableId', response.data.tableId, form);
      } else {
        throw new Error('返回数据格式不正确');
      }
    } catch (error: any) {
      console.error('获取表格元数据失败:', error);
      setMetaDataError(`获取表格元数据失败: ${error?.message || '请检查表格链接是否正确'}`);
      setTableMetaData(null);
    } finally {
      setIsLoadingMetaData(false);
    }
  };

  // 组件挂载时检查是否已有飞书链接，如果有则自动获取元数据
  useEffect(() => {
    const initialLink = getFieldValue('link', form);
    const savedObjToken = getFieldValue('objToken', form);
    const savedTableId = getFieldValue('tableId', form);

    if (initialLink && initialLink.trim()) {
      setLink(initialLink);

      // 如果有保存的元数据，先恢复显示
      if (savedObjToken && savedTableId) {
        // 从保存的字段重构元数据对象
        const reconstructedMetaData = {
          objToken: savedObjToken,
          tableId: savedTableId,
          tableName: '', // 这些字段需要重新获取
          viewId: '',
          columnNames: [],
        };
        setTableMetaData(reconstructedMetaData);
      }

      // 延迟执行以确保状态已更新
      // setTimeout(() => {
      //   handleLinkBlur();
      // }, 100);
    }
  }, []);

  if (isSidebar) {
    return (
      <>
        <FormHeader />
        <FormContent>
          <Field
            name="link"
            render={({ field }) => (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <span>飞书通知链接</span>
                  <IconHelpCircle
                    style={{ marginLeft: 4, color: '#1890ff', cursor: 'pointer' }}
                    title="填入飞书通知链接，既作为机器人Webhook地址"
                  />
                </div>
                <TextArea
                  {...field}
                  value={link}
                  onChange={(value) => {
                    setLink(value);
                    setFieldValue('link', value, form);
                  }}
                  // onBlur={handleLinkBlur}
                  placeholder="请输入飞书通知链接"
                />

                {/* 表格元数据展示区域 */}
                {isLoadingMetaData && (
                  <div
                    style={{ marginTop: 12, display: 'flex', alignItems: 'center', color: '#666' }}
                  >
                    <Spin size="small" style={{ marginRight: 8 }} />
                    <span>正在获取表格元数据...</span>
                  </div>
                )}

                {!isLoadingMetaData && metaDataError && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 8,
                      backgroundColor: '#fff2f0',
                      border: '1px solid #ffccc7',
                      borderRadius: 4,
                      color: '#ff4d4f',
                    }}
                  >
                    {metaDataError}
                  </div>
                )}

                {!isLoadingMetaData && !metaDataError && tableMetaData && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 'bold', color: '#52c41a' }}>表格名称: </span>
                      <span>{tableMetaData.tableName}</span>
                    </div>
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>表头: </span>
                        {tableMetaData.columnNames && tableMetaData.columnNames.length > 0 && (
                          <Button
                            icon={<IconCopy />}
                            size="small"
                            type="tertiary"
                            onClick={copyAllFieldNames}
                            style={{ fontSize: '12px' }}
                          >
                            复制全部
                          </Button>
                        )}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {tableMetaData.columnNames && tableMetaData.columnNames.length > 0 ? (
                          tableMetaData.columnNames.map((column: string, index: number) => (
                            <div
                              key={index}
                              style={{
                                display: 'inline-block',
                                marginRight: 4,
                                marginBottom: 4,
                              }}
                              title={`点击复制字段名: ${column}`}
                            >
                              <Tag
                                color="blue"
                                style={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                                onClick={() => copyFieldName(column)}
                              >
                                {column}
                                <IconCopy style={{ marginLeft: 4, fontSize: '12px' }} />
                              </Tag>
                            </div>
                          ))
                        ) : (
                          <span style={{ color: '#999' }}>无表头数据</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          />

          <Field
            name="jsCode"
            render={() => (
              <div>
                <div style={{ marginBottom: 8 }}>JavaScript代码</div>
                <CodeEditor
                  value={jsCode}
                  mode={{ name: 'javascript', json: true }}
                  placeholder="请输入 JavaScript 代码..."
                  enableFormat={true}
                  autoHeight={true}
                  onChange={(value) => {
                    setJsCode(value);
                    setFieldValue('jsCode', value, form);
                  }}
                />
              </div>
            )}
          />
          {pushResult && <div>推送结果: {pushResult}</div>}
        </FormContent>
      </>
    );
  }
  return (
    <>
      <FormHeader />
      <FormContent>
        <FormOutputs />
        <FormItem name="Link" type="string">
          {link}
        </FormItem>
        <FormItem name="JS Code" type="string">
          {jsCode}
        </FormItem>
      </FormContent>
    </>
  );
};

export const formMeta: FormMeta<FlowNodeJSON> = {
  render: renderForm,
  validateTrigger: ValidateTrigger.onChange,
  validate: {
    fields: ({ value }) => {
      if (!value[0].value) return '飞书通知链接不能为空';
      return undefined;
    },
  },
};
